import * as React from "react";
import { CircleDashed } from "lucide-react";
import { cn } from "@eduviaos/ui-kit";

/**
 * Phase F-finale-6 Slice 6b — banner rendered on the IA admission-funnel
 * editor when the SA has paused funnel editing for the tenant
 * (`feature_flags.admissions_editable_by_tenant === false`).
 *
 * Mirrors `TenantFormsPausedBanner` (Slice 5a): informational amber tone,
 * not destructive. Reads stay available (the funnel still drives the live
 * admissions board) — only the editor write surface is gated.
 */
export interface TenantAdmissionsPausedBannerProps {
  className?: string;
  body?: React.ReactNode;
}

export function TenantAdmissionsPausedBanner({
  className,
  body,
}: TenantAdmissionsPausedBannerProps) {
  return (
    <div
      role="status"
      data-testid="admissions-editing-paused-banner"
      className={cn(
        "border-amber-500/30 bg-amber-500/5 text-amber-900 dark:text-amber-200",
        "flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs",
        className,
      )}
    >
      <CircleDashed
        aria-hidden="true"
        className="mt-0.5 size-3.5 shrink-0 text-amber-600 dark:text-amber-300"
      />
      <div className="flex-1 leading-relaxed">
        {body ?? (
          <>
            <span className="font-medium">Admission-funnel editing is paused.</span> Your account
            manager has temporarily disabled funnel editing for your institution. Your funnel still
            drives the live admissions board — you just can&apos;t change its stages, policies, or
            requirements right now. Contact your account manager to re-enable editing.
          </>
        )}
      </div>
    </div>
  );
}
